// «Comparar tiendas» (ADR-0167) — the table the IA screen shows under
// the «Comparar tiendas» chip and under a chat answer that asked for one.
// Every cell comes from core-api (GET /assets/compare, or the chat's
// `table`); this component only sorts, folds and exports it.
//
// Accessibility: a real <table> with <caption> and scoped headers; the
// sort lives on header buttons with aria-sort; the trend reads as words
// to a screen reader, never as color or an arrow alone; export progress
// and results land in a polite status region next to the menu.

import { useEffect, useMemo, useRef, useState } from "react";
import type {
  StoreComparisonColumn,
  StoreComparisonColumnKey,
  StoreComparisonQuery,
  StoreComparisonResponse,
} from "@bitacorapro/api-types";
import { getCompareStoresXlsx } from "../lib/api/endpoints/ia";
import {
  asOfStamp,
  buildCompareCsv,
  compareCells,
  compareFilename,
  formatCell,
  isMaintenanceColumn,
} from "../lib/compare-stores";
import { downloadBlob, downloadCsv } from "../lib/csv";
import { Icon } from "./icons";
import { ENTITY_ROUTES, UUID_RE } from "./MarkdownMessage";

const COLLAPSED_ROWS = 10;

type Sort = { key: StoreComparisonColumnKey; dir: "asc" | "desc" } | null;

export function CompareStoresTable({
  table,
  query,
}: {
  table: StoreComparisonResponse;
  query: StoreComparisonQuery;
}) {
  const hasScore = table.columns.some((c) => c.key === "score");
  const [sort, setSort] = useState<Sort>(hasScore ? { key: "score", dir: "asc" } : null);
  // A query that named a maintenance column (a chat table asked for
  // «mantenciones vencidas») opens with the group shown.
  const [showMaint, setShowMaint] = useState(
    () => query.columns?.some((k) => k.startsWith("mant_")) ?? false
  );
  const [expanded, setExpanded] = useState(false);

  const sortedRows = useMemo(() => {
    if (!sort) return table.rows;
    return [...table.rows].sort((a, b) =>
      compareCells(a.cells[sort.key] ?? null, b.cells[sort.key] ?? null, sort.dir)
    );
  }, [table.rows, sort]);

  const maintCols = table.columns.filter(isMaintenanceColumn);
  // The maintenance group exists only when some store carries an item.
  const hasMaintData =
    maintCols.length > 0 &&
    table.rows.some((r) => maintCols.some((c) => (r.cells[c.key] ?? null) !== null));
  // The store first (it is the sticky column), then the server's order.
  const storeCol = table.columns.find((c) => c.kind === "store");
  const visibleCols = [
    ...(storeCol ? [storeCol] : []),
    ...table.columns.filter(
      (c) => c !== storeCol && (!isMaintenanceColumn(c) || (hasMaintData && showMaint))
    ),
  ];

  const lastReportCol = table.columns.find((c) => c.key === "ultimo_reporte");
  const noReports =
    table.rows.length === 0 ||
    (lastReportCol !== undefined &&
      table.rows.every((r) => (r.cells[lastReportCol.key] ?? null) === null));
  if (noReports) {
    return (
      <div className="ia-cmp">
        <p className="ia-cmp__empty">
          Aún no hay tiendas con reportes cerrados en tu alcance para comparar.
        </p>
      </div>
    );
  }

  const shownRows = expanded ? sortedRows : sortedRows.slice(0, COLLAPSED_ROWS);
  const hasGaps = shownRows.some((r) => visibleCols.some((c) => (r.cells[c.key] ?? null) === null));
  const partial = table.rows.length < table.scopeTotal;

  function toggleSort(key: StoreComparisonColumnKey) {
    setSort((prev) =>
      prev?.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  }

  return (
    <div className="ia-cmp">
      {partial && (
        <p className="ia-cmp__notice">
          Estás viendo {table.rows.length} de {table.scopeTotal} tiendas de tu alcance. El archivo
          incluirá solo esas.
        </p>
      )}
      <div className="ia-cmp__bar">
        <span className="ia-cmp__origin">Datos de tu cuenta · al {asOfStamp(table.asOf)}</span>
        <div className="ia-cmp__actions">
          {hasMaintData && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              aria-expanded={showMaint}
              onClick={() => setShowMaint((v) => !v)}
            >
              {showMaint ? "Ocultar mantenciones" : "Mostrar mantenciones"}
            </button>
          )}
          <ExportMenu table={table} query={query} sort={sort} rows={sortedRows} />
        </div>
      </div>

      <div className="dtable-scroll ia-cmp__scroll" tabIndex={0} aria-label="Tabla desplazable">
        <table className="dtable dtable--compact ia-cmp__table">
          <caption className="sr-only">Comparación de tiendas</caption>
          <thead>
            <tr>
              {visibleCols.map((c) => (
                <HeaderCell key={c.key} col={c} sort={sort} onSort={toggleSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {shownRows.map((r) => (
              <tr key={r.assetId}>
                {visibleCols.map((c) => (
                  <BodyCell
                    key={c.key}
                    col={c}
                    value={r.cells[c.key] ?? null}
                    assetId={r.assetId}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasGaps && <p className="ia-cmp__foot">— sin dato en el último reporte</p>}
      {!expanded && sortedRows.length > COLLAPSED_ROWS && (
        <button type="button" className="ia-retry-btn" onClick={() => setExpanded(true)}>
          Ver las {sortedRows.length} tiendas
        </button>
      )}
    </div>
  );
}

function HeaderCell({
  col,
  sort,
  onSort,
}: {
  col: StoreComparisonColumn;
  sort: Sort;
  onSort: (key: StoreComparisonColumnKey) => void;
}) {
  const active = sort?.key === col.key;
  const ariaSort = active ? (sort.dir === "asc" ? "ascending" : "descending") : "none";
  const numeric = col.kind === "number" || col.kind === "percent";
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={[numeric ? "col-num" : "", col.kind === "store" ? "ia-cmp__sticky" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      <button type="button" className="ia-cmp__sort" onClick={() => onSort(col.key)}>
        {col.label}
        <span className="ia-cmp__sort-ind" aria-hidden="true">
          {active ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
        </span>
      </button>
    </th>
  );
}

function BodyCell({
  col,
  value,
  assetId,
}: {
  col: StoreComparisonColumn;
  value: string | number | null;
  assetId: string;
}) {
  const numeric = col.kind === "number" || col.kind === "percent";
  if (col.kind === "store") {
    const name = value === null ? "—" : String(value);
    return (
      <th scope="row" className="ia-cmp__sticky ia-cmp__store">
        {UUID_RE.test(assetId) ? (
          <a
            className="ia-entity-link"
            href={`${ENTITY_ROUTES.store}${assetId}`}
            target="_blank"
            rel="noopener"
            aria-label={`${name} (abre en pestaña nueva)`}
          >
            {name}
          </a>
        ) : (
          name
        )}
      </th>
    );
  }
  if (value === null) {
    return (
      <td className={numeric ? "col-num" : undefined}>
        <span aria-hidden="true">—</span>
        <span className="sr-only">sin dato</span>
      </td>
    );
  }
  if (col.key === "tendencia" && typeof value === "number") {
    const [arrow, word, sign] =
      value > 0 ? ["▲", "sube", "+"] : value < 0 ? ["▼", "baja", "−"] : ["=", "sin cambio", ""];
    return (
      <td className="col-num ia-cmp__trend">
        <span aria-hidden="true">
          {arrow} {sign}
          {formatCell(col, value)}
        </span>
        <span className="sr-only">
          {word} {formatCell(col, value)}
        </span>
      </td>
    );
  }
  return <td className={numeric ? "col-num" : undefined}>{formatCell(col, value)}</td>;
}

type ExportState =
  | { status: "idle" }
  | { status: "busy" }
  | { status: "done"; filename: string }
  | { status: "error" };

const FORMAT_LABEL = { xlsx: "Excel", csv: "CSV" } as const;

function ExportMenu({
  table,
  query,
  sort,
  rows,
}: {
  table: StoreComparisonResponse;
  query: StoreComparisonQuery;
  sort: Sort;
  rows: StoreComparisonResponse["rows"];
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // Escape or a click outside closes the menu; Escape hands focus back to
  // the trigger so keyboard users stay where they were.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
    function onPointer(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open]);
  const [state, setState] = useState<Record<"xlsx" | "csv", ExportState>>({
    xlsx: { status: "idle" },
    csv: { status: "idle" },
  });
  // The last format touched owns the inline message under the menu.
  const [last, setLast] = useState<"xlsx" | "csv" | null>(null);

  async function run(format: "xlsx" | "csv") {
    setLast(format);
    setState((s) => ({ ...s, [format]: { status: "busy" } }));
    try {
      let filename: string;
      if (format === "xlsx") {
        // The workbook is re-read at export time (ADR-0167), in the
        // order on screen; the server names it after its own asOf.
        const download = await getCompareStoresXlsx({
          ...query,
          ...(sort ? { sortBy: sort.key, sortDir: sort.dir } : {}),
        });
        filename = download.filename ?? compareFilename(new Date().toISOString(), "xlsx");
        downloadBlob(download.blob, filename);
      } else {
        // The CSV is the table on screen: named after its asOf.
        filename = compareFilename(table.asOf, "csv");
        downloadCsv(buildCompareCsv(table, rows), filename);
      }
      setState((s) => ({ ...s, [format]: { status: "done", filename } }));
    } catch {
      setState((s) => ({ ...s, [format]: { status: "error" } }));
    }
  }

  const current = last ? state[last] : null;
  return (
    <div className="ia-cmp__export" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="btn btn--ghost btn--sm"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon.Download s={13} /> Exportar
      </button>
      {open && (
        <div className="ia-cmp__menu" role="group" aria-label="Formatos de exportación">
          {(["xlsx", "csv"] as const).map((f) => (
            <button
              key={f}
              type="button"
              className="ia-cmp__menu-item"
              disabled={state[f].status === "busy"}
              onClick={() => void run(f)}
            >
              {state[f].status === "busy"
                ? `Preparando ${FORMAT_LABEL[f]}…`
                : f === "xlsx"
                  ? "Excel (.xlsx)"
                  : "CSV (.csv)"}
            </button>
          ))}
        </div>
      )}
      <div className="ia-cmp__status" role="status">
        {last && current?.status === "done" && (
          <>
            Descargaste {current.filename}
            {last === "xlsx" ? " (Excel con datos al momento de exportar)" : ""}.{" "}
            <button type="button" className="ia-retry-btn" onClick={() => void run(last)}>
              Descargar de nuevo
            </button>
          </>
        )}
        {last && current?.status === "error" && (
          <>
            No pudimos preparar el archivo. Tus datos no se perdieron. Intenta de nuevo.{" "}
            <button type="button" className="ia-retry-btn" onClick={() => void run(last)}>
              Reintentar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
